import * as THREE from 'three';

// ============================================================
// EntityAnimations.js - Enhanced animation system for entities
// ============================================================
// Provides rich, smooth animations for all entity types:
//   walk, idle, attack, death, hurt, swim, baby
//
// Usage from EntityManager:
//   import { updateEntityAnimation, triggerAttack, triggerHurt, triggerDeath, setSwimming } from './EntityAnimations.js';
//   // In update loop:
//   updateEntityAnimation(entity, dt);
//   // On events:
//   triggerAttack(entity);
//   triggerHurt(entity);
//   triggerDeath(entity);
//   setSwimming(entity, true);
// ============================================================

// --- Reusable objects (avoid per-frame allocations) ---
const _hurtRedColor = new THREE.Color(0xFF2222);
const _tmpColor = new THREE.Color();

// --- Utility ---

/** Smoothly interpolate toward a target value */
function lerp(current, target, t) {
  return current + (target - current) * Math.min(t, 1);
}

/** Ease-out quadratic for snappy attack feel */
function easeOutQuad(t) {
  return t * (2 - t);
}

/** Ease-in-out for smooth oscillations */
function easeInOutSine(t) {
  return 0.5 * (1 - Math.cos(Math.PI * t));
}

// --- Constants ---

const HURT_DURATION = 0.2;       // seconds of red flash
const ATTACK_DURATION = 0.3;     // seconds for full attack swing
const DEATH_DURATION = 0.5;      // seconds to fall over
const DEATH_FADE_DELAY = 0.3;    // additional seconds before opacity fade starts
const DEATH_FADE_DURATION = 0.5; // seconds to fade out after fall

// Walk animation parameters
const WALK_LEG_SPEED = 8;
const WALK_LEG_AMP = 0.5;
const WALK_ARM_AMP = 0.6;
const WALK_BODY_BOB_AMP = 0.02;
const WALK_HEAD_BOB_AMP = 0.04;

// Idle animation parameters
const IDLE_BREATH_SPEED = 1.5;
const IDLE_BREATH_AMP = 0.015;
const IDLE_HEAD_LOOK_INTERVAL_MIN = 2.0;
const IDLE_HEAD_LOOK_INTERVAL_MAX = 5.0;
const IDLE_HEAD_LOOK_SPEED = 1.5;
const IDLE_HEAD_LOOK_AMP = 0.4;
const IDLE_TAIL_SPEED = 3.0;
const IDLE_TAIL_AMP = 0.25;

// Swim animation parameters
const SWIM_BODY_TILT = -0.5;     // forward tilt in radians (~30 deg)
const SWIM_ARM_SPEED = 4;
const SWIM_ARM_AMP_X = 1.2;
const SWIM_ARM_AMP_Z = 0.6;
const SWIM_LEG_SPEED = 3;
const SWIM_LEG_AMP = 0.3;

// Baby walk parameters (exaggerated)
const BABY_HEAD_BOB_AMP = 0.08;
const BABY_LEG_SPEED = 10;
const BABY_LEG_AMP = 0.6;

// Types that are humanoid (have arms, no back legs)
const HUMANOID_TYPES = new Set(['VILLAGER', 'STEVE', 'ALEX', 'HEROBRINE', 'ZOMBIE', 'SKELETON']);

// Types that are animals (have tails, four legs)
const ANIMAL_TYPES = new Set(['PIG', 'SHEEP', 'COW', 'CHICKEN', 'WOLF']);

// ============================================================
// Animation State
// ============================================================

/**
 * Ensures the entity has an _animState object. Creates one if missing.
 * @param {Object} entity
 * @returns {Object} the _animState
 */
function ensureAnimState(entity) {
  if (!entity._animState) {
    entity._animState = {
      action: 'idle',       // 'idle' | 'walk' | 'attack' | 'death' | 'hurt' | 'swim'
      actionTime: 0,
      hurtTimer: 0,
      deathProgress: 0,
      attackProgress: 0,
      breathPhase: Math.random() * Math.PI * 2, // randomize breathing start
      headLookTimer: IDLE_HEAD_LOOK_INTERVAL_MIN + Math.random() * (IDLE_HEAD_LOOK_INTERVAL_MAX - IDLE_HEAD_LOOK_INTERVAL_MIN),
      headLookTarget: 0,
      headLookCurrent: 0,
      originalColors: null,  // cached for hurt flash restore
      deathFadeStarted: false,
    };
  }
  return entity._animState;
}

// ============================================================
// Trigger functions (exported)
// ============================================================

/**
 * Triggers the attack animation on an entity.
 * The right arm (or head for animals) will swing forward.
 */
export function triggerAttack(entity) {
  const state = ensureAnimState(entity);
  if (state.action === 'death') return; // can't attack while dying
  state.action = 'attack';
  state.attackProgress = 0;
}

/**
 * Triggers the hurt flash on an entity.
 * Entity meshes briefly turn red, then restore.
 */
export function triggerHurt(entity) {
  const state = ensureAnimState(entity);
  if (state.action === 'death') return;
  state.hurtTimer = HURT_DURATION;
}

/**
 * Triggers the death animation on an entity.
 * Entity falls sideways, flashes red, then fades out.
 */
export function triggerDeath(entity) {
  const state = ensureAnimState(entity);
  state.action = 'death';
  state.deathProgress = 0;
  state.deathFadeStarted = false;
  // Also flash red at the start of death
  state.hurtTimer = HURT_DURATION;
}

/**
 * Toggles swimming animation on/off.
 */
export function setSwimming(entity, isSwimming) {
  const state = ensureAnimState(entity);
  if (state.action === 'death') return;
  if (isSwimming && state.action !== 'swim') {
    state.action = 'swim';
    state.actionTime = 0;
  } else if (!isSwimming && state.action === 'swim') {
    state.action = 'idle';
    state.actionTime = 0;
  }
}

// ============================================================
// Main update function (exported)
// ============================================================

/**
 * Updates the animation for a single entity based on its current state.
 * Call this every frame from EntityManager's update loop.
 *
 * @param {Object} entity - Entity with mesh references (legFL, legFR, etc.)
 * @param {number} dt - Delta time in seconds
 */
export function updateEntityAnimation(entity, dt) {
  if (!entity.mesh) return;
  // Clamp dt to prevent animation jumps after tab-switch
  dt = Math.min(dt, 0.1);

  const state = ensureAnimState(entity);
  state.actionTime += dt;

  // --- Determine action from movement if not in a special state ---
  if (state.action !== 'death' && state.action !== 'attack' && state.action !== 'swim') {
    const isMoving = entity.velocity &&
      (Math.abs(entity.velocity.x) > 0.01 || Math.abs(entity.velocity.z) > 0.01);
    if (isMoving) {
      if (state.action !== 'walk') {
        state.action = 'walk';
        state.actionTime = 0;
      }
    } else {
      if (state.action !== 'idle') {
        state.action = 'idle';
        state.actionTime = 0;
      }
    }
  }

  // --- Dispatch to specific animation ---
  switch (state.action) {
    case 'walk':
      animateWalk(entity, dt, state);
      break;
    case 'idle':
      animateIdle(entity, dt, state);
      break;
    case 'attack':
      animateAttack(entity, dt, state);
      break;
    case 'death':
      animateDeath(entity, dt, state);
      break;
    case 'swim':
      animateSwim(entity, dt, state);
      break;
  }

  // --- Hurt flash overlay (runs independently of action) ---
  if (state.hurtTimer > 0) {
    animateHurt(entity, dt, state);
  } else if (state.originalColors) {
    // Restore original colors if hurt just ended
    restoreColors(entity, state);
  }
}

// ============================================================
// Animation implementations
// ============================================================

/**
 * Enhanced walking animation:
 * - Legs swing with alternating pairs
 * - Arms swing opposite to legs (humanoids)
 * - Slight body bob (vertical oscillation on root mesh)
 * - Head slight bob
 */
function animateWalk(entity, dt, state) {
  const t = entity.animTime || state.actionTime;
  const speed = WALK_LEG_SPEED;
  const legAmp = WALK_LEG_AMP;

  // Leg swing (front-left and back-right in phase, front-right and back-left opposite)
  if (entity.legFL) {
    entity.legFL.rotation.x = Math.sin(t * speed) * legAmp;
  }
  if (entity.legFR) {
    entity.legFR.rotation.x = -Math.sin(t * speed) * legAmp;
  }
  if (entity.legBL) {
    entity.legBL.rotation.x = -Math.sin(t * speed) * legAmp;
  }
  if (entity.legBR) {
    entity.legBR.rotation.x = Math.sin(t * speed) * legAmp;
  }

  // Arm swing (opposite to corresponding leg side, humanoids only)
  if (entity.armL) {
    entity.armL.rotation.x = -Math.sin(t * speed) * WALK_ARM_AMP;
  }
  if (entity.armR) {
    entity.armR.rotation.x = Math.sin(t * speed) * WALK_ARM_AMP;
  }

  // Body vertical bob (double frequency of legs, subtle)
  if (entity.mesh) {
    // Store the original Y if not already stored
    if (entity._baseY === undefined) {
      entity._baseY = 0; // Bob offset, not absolute position (mesh.position.y is set by physics)
    }
    entity._bodyBobOffset = Math.abs(Math.sin(t * speed)) * WALK_BODY_BOB_AMP;
    // Apply as a child offset - we shift all children up slightly rather than moving the root
    // to avoid conflicting with physics position updates.
    // Instead, we use a small Y-scale pulse on the root which creates a similar effect.
  }

  // Head bob (slight nod)
  if (entity.headMesh) {
    entity.headMesh.rotation.x = Math.sin(t * speed * 2) * WALK_HEAD_BOB_AMP;
    // Reset any idle look-around rotation smoothly
    entity.headMesh.rotation.y = lerp(entity.headMesh.rotation.y, 0, dt * 4);
  }

  // Reset body tilt from swimming
  _resetBodyTilt(entity, dt);
}

/**
 * Idle animation:
 * - Breathing: subtle body scale pulse on Y axis
 * - Head look around: small Y rotation at random intervals
 * - Tail wag for animals
 * - Legs return to rest position smoothly
 */
function animateIdle(entity, dt, state) {
  // --- Breathing ---
  state.breathPhase += dt * IDLE_BREATH_SPEED;
  const breathScale = 1.0 + Math.sin(state.breathPhase) * IDLE_BREATH_AMP;

  // Apply breathing as a very subtle Y-scale on the root mesh
  if (entity.mesh) {
    entity.mesh.scale.y = lerp(entity.mesh.scale.y, breathScale, dt * 5);
    entity.mesh.scale.x = lerp(entity.mesh.scale.x, 1.0, dt * 5);
    entity.mesh.scale.z = lerp(entity.mesh.scale.z, 1.0, dt * 5);
  }

  // --- Head look around ---
  state.headLookTimer -= dt;
  if (state.headLookTimer <= 0) {
    // Pick a new random target rotation
    state.headLookTarget = (Math.random() - 0.5) * IDLE_HEAD_LOOK_AMP * 2;
    state.headLookTimer = IDLE_HEAD_LOOK_INTERVAL_MIN +
      Math.random() * (IDLE_HEAD_LOOK_INTERVAL_MAX - IDLE_HEAD_LOOK_INTERVAL_MIN);
  }

  if (entity.headMesh) {
    state.headLookCurrent = lerp(state.headLookCurrent, state.headLookTarget, dt * IDLE_HEAD_LOOK_SPEED);
    entity.headMesh.rotation.y = state.headLookCurrent;
    // Return pitch to neutral
    entity.headMesh.rotation.x = lerp(entity.headMesh.rotation.x, 0, dt * 3);
  }

  // --- Smoothly return legs to rest ---
  if (entity.legFL) entity.legFL.rotation.x = lerp(entity.legFL.rotation.x, 0, dt * 6);
  if (entity.legFR) entity.legFR.rotation.x = lerp(entity.legFR.rotation.x, 0, dt * 6);
  if (entity.legBL) entity.legBL.rotation.x = lerp(entity.legBL.rotation.x, 0, dt * 6);
  if (entity.legBR) entity.legBR.rotation.x = lerp(entity.legBR.rotation.x, 0, dt * 6);

  // --- Smoothly return arms to rest ---
  if (entity.armL) entity.armL.rotation.x = lerp(entity.armL.rotation.x, 0, dt * 6);
  if (entity.armR) entity.armR.rotation.x = lerp(entity.armR.rotation.x, 0, dt * 6);

  // --- Tail wag for animals ---
  if (ANIMAL_TYPES.has(entity.type)) {
    _animateTailWag(entity, state);
  }

  // Reset body tilt from swimming
  _resetBodyTilt(entity, dt);
}

/**
 * Attack animation:
 * - Right arm swings forward (rotation.x from 0 to -1.5 and back)
 * - For animals without arms, head lunges forward
 * - Duration: ATTACK_DURATION seconds, then returns to previous state
 */
function animateAttack(entity, dt, state) {
  state.attackProgress += dt / ATTACK_DURATION;

  if (state.attackProgress >= 1.0) {
    // Attack finished, return to idle/walk
    state.attackProgress = 0;
    state.action = 'idle';
    state.actionTime = 0;
    // Reset arm
    if (entity.armR) entity.armR.rotation.x = 0;
    if (entity.headMesh) entity.headMesh.rotation.x = 0;
    return;
  }

  const progress = state.attackProgress;

  if (entity.armR) {
    // Humanoid attack: right arm swings forward then back
    // Use a curve that goes from 0 -> -1.5 -> 0
    let armAngle;
    if (progress < 0.4) {
      // Wind up: 0 to -1.5
      armAngle = -1.5 * easeOutQuad(progress / 0.4);
    } else {
      // Return: -1.5 to 0
      armAngle = -1.5 * (1.0 - easeOutQuad((progress - 0.4) / 0.6));
    }
    entity.armR.rotation.x = armAngle;

    // Left arm stays relatively still during attack
    if (entity.armL) {
      entity.armL.rotation.x = lerp(entity.armL.rotation.x, 0.3, dt * 8);
    }
  } else if (entity.headMesh) {
    // Animal attack: head lunges forward
    let headAngle;
    if (progress < 0.3) {
      headAngle = -0.6 * easeOutQuad(progress / 0.3);
    } else {
      headAngle = -0.6 * (1.0 - easeOutQuad((progress - 0.3) / 0.7));
    }
    entity.headMesh.rotation.x = headAngle;
  }

  // Keep legs animating if entity was moving
  const isMoving = entity.velocity &&
    (Math.abs(entity.velocity.x) > 0.01 || Math.abs(entity.velocity.z) > 0.01);
  if (isMoving) {
    const t = entity.animTime || state.actionTime;
    if (entity.legFL) entity.legFL.rotation.x = Math.sin(t * WALK_LEG_SPEED) * WALK_LEG_AMP;
    if (entity.legFR) entity.legFR.rotation.x = -Math.sin(t * WALK_LEG_SPEED) * WALK_LEG_AMP;
    if (entity.legBL) entity.legBL.rotation.x = -Math.sin(t * WALK_LEG_SPEED) * WALK_LEG_AMP;
    if (entity.legBR) entity.legBR.rotation.x = Math.sin(t * WALK_LEG_SPEED) * WALK_LEG_AMP;
  }
}

/**
 * Death animation:
 * - Entity tilts sideways (rotation.z -> PI/2 over DEATH_DURATION)
 * - Flashes red at the start (handled by hurt overlay)
 * - After falling, fades opacity to 0
 */
function animateDeath(entity, dt, state) {
  state.deathProgress += dt;

  if (!entity.mesh) return;

  // Phase 1: Tilt sideways
  if (state.deathProgress < DEATH_DURATION) {
    const tiltProgress = easeOutQuad(state.deathProgress / DEATH_DURATION);
    entity.mesh.rotation.z = tiltProgress * (Math.PI / 2);
  } else {
    entity.mesh.rotation.z = Math.PI / 2;
  }

  // Phase 2: Fade out opacity
  const fadeStart = DEATH_DURATION + DEATH_FADE_DELAY;
  if (state.deathProgress > fadeStart) {
    if (!state.deathFadeStarted) {
      // Enable transparency on all materials
      _setMeshTransparent(entity.mesh, true);
      state.deathFadeStarted = true;
    }

    const fadeProgress = Math.min((state.deathProgress - fadeStart) / DEATH_FADE_DURATION, 1.0);
    const opacity = 1.0 - fadeProgress;
    _setMeshOpacity(entity.mesh, opacity);
  }

  // Stop all limb movement during death
  if (entity.legFL) entity.legFL.rotation.x = lerp(entity.legFL.rotation.x, 0, dt * 8);
  if (entity.legFR) entity.legFR.rotation.x = lerp(entity.legFR.rotation.x, 0, dt * 8);
  if (entity.legBL) entity.legBL.rotation.x = lerp(entity.legBL.rotation.x, 0, dt * 8);
  if (entity.legBR) entity.legBR.rotation.x = lerp(entity.legBR.rotation.x, 0, dt * 8);
  if (entity.armL) entity.armL.rotation.x = lerp(entity.armL.rotation.x, 0, dt * 8);
  if (entity.armR) entity.armR.rotation.x = lerp(entity.armR.rotation.x, 0, dt * 8);
}

/**
 * Hurt flash animation:
 * - All meshes briefly turn red for HURT_DURATION seconds
 * - Original colors are cached and restored afterward
 */
function animateHurt(entity, dt, state) {
  state.hurtTimer -= dt;

  if (!entity.mesh) return;

  // Cache original colors on first hurt frame
  if (!state.originalColors) {
    state.originalColors = [];
    entity.mesh.traverse((child) => {
      if (child.isMesh && child.material) {
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        const saved = [];
        for (const mat of materials) {
          if (mat.color) {
            saved.push({ material: mat, color: mat.color.getHex() });
          }
        }
        if (saved.length > 0) {
          state.originalColors.push(...saved);
        }
      }
    });
  }

  // Flash red: interpolate between red and original based on a pulsing function
  // Rapid pulse for more dramatic effect
  const flashIntensity = Math.abs(Math.sin(state.hurtTimer / HURT_DURATION * Math.PI * 3));

  entity.mesh.traverse((child) => {
    if (child.isMesh && child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of materials) {
        if (mat.color) {
          const original = _findOriginalColor(state.originalColors, mat);
          if (original !== null) {
            _tmpColor.setHex(original);
            mat.color.copy(_tmpColor).lerp(_hurtRedColor, flashIntensity * 0.7);
          }
        }
      }
    }
  });
}

/**
 * Swimming animation:
 * - Body tilts forward (rotation.x toward SWIM_BODY_TILT)
 * - Arms do breaststroke motion (alternating forward/outward sweep)
 * - Legs kick gently
 */
function animateSwim(entity, dt, state) {
  const t = state.actionTime;

  if (!entity.mesh) return;

  // Body tilt forward
  // Store the tilt separately so it doesn't conflict with mesh rotation.y (facing direction)
  // We apply the tilt to all children's parent offset instead
  if (entity.mesh._swimTiltApplied === undefined) {
    entity.mesh._swimTiltApplied = 0;
  }
  entity.mesh._swimTiltApplied = lerp(entity.mesh._swimTiltApplied, SWIM_BODY_TILT, dt * 3);

  // We can safely adjust rotation.x on the root group since rotation.y is facing direction
  entity.mesh.rotation.x = entity.mesh._swimTiltApplied;

  // Arm breaststroke (humanoids only)
  if (entity.armL) {
    // Breaststroke: arms go forward then sweep outward/back
    const armPhase = t * SWIM_ARM_SPEED;
    entity.armL.rotation.x = Math.sin(armPhase) * SWIM_ARM_AMP_X;
    entity.armL.rotation.z = Math.cos(armPhase) * SWIM_ARM_AMP_Z + SWIM_ARM_AMP_Z;
  }
  if (entity.armR) {
    const armPhase = t * SWIM_ARM_SPEED;
    entity.armR.rotation.x = Math.sin(armPhase) * SWIM_ARM_AMP_X;
    entity.armR.rotation.z = -(Math.cos(armPhase) * SWIM_ARM_AMP_Z + SWIM_ARM_AMP_Z);
  }

  // Leg kick (gentle flutter)
  if (entity.legFL) {
    entity.legFL.rotation.x = Math.sin(t * SWIM_LEG_SPEED) * SWIM_LEG_AMP;
  }
  if (entity.legFR) {
    entity.legFR.rotation.x = Math.sin(t * SWIM_LEG_SPEED + Math.PI) * SWIM_LEG_AMP;
  }
  if (entity.legBL) {
    entity.legBL.rotation.x = Math.sin(t * SWIM_LEG_SPEED + Math.PI) * SWIM_LEG_AMP;
  }
  if (entity.legBR) {
    entity.legBR.rotation.x = Math.sin(t * SWIM_LEG_SPEED) * SWIM_LEG_AMP;
  }

  // Head stays level (counters body tilt a bit)
  if (entity.headMesh) {
    entity.headMesh.rotation.x = lerp(entity.headMesh.rotation.x, -SWIM_BODY_TILT * 0.5, dt * 3);
  }
}

/**
 * Baby walk animation:
 * - Same as walk but with exaggerated head bob (big heads)
 * - Faster, shorter leg movements
 */
export function animateBaby(entity, dt) {
  const state = ensureAnimState(entity);
  const t = entity.animTime || state.actionTime;

  const isMoving = entity.velocity &&
    (Math.abs(entity.velocity.x) > 0.01 || Math.abs(entity.velocity.z) > 0.01);

  if (!isMoving) {
    // Idle for baby - just breathing and head look
    animateIdle(entity, dt, state);
    return;
  }

  const speed = BABY_LEG_SPEED;
  const legAmp = BABY_LEG_AMP;

  // Legs: faster and wider swing
  if (entity.legFL) entity.legFL.rotation.x = Math.sin(t * speed) * legAmp;
  if (entity.legFR) entity.legFR.rotation.x = -Math.sin(t * speed) * legAmp;
  if (entity.legBL) entity.legBL.rotation.x = -Math.sin(t * speed) * legAmp;
  if (entity.legBR) entity.legBR.rotation.x = Math.sin(t * speed) * legAmp;

  // Arms (humanoids)
  if (entity.armL) entity.armL.rotation.x = -Math.sin(t * speed) * WALK_ARM_AMP;
  if (entity.armR) entity.armR.rotation.x = Math.sin(t * speed) * WALK_ARM_AMP;

  // Exaggerated head bob (babies have big heads in Minecraft)
  if (entity.headMesh) {
    entity.headMesh.rotation.x = Math.sin(t * speed * 2) * BABY_HEAD_BOB_AMP;
    // Slight side-to-side wobble
    entity.headMesh.rotation.z = Math.sin(t * speed) * 0.05;
  }
}

// ============================================================
// Internal helpers
// ============================================================

/**
 * Animate tail wagging for animal entities.
 * The tail is found by looking for a child mesh positioned at the back of the entity
 * (negative Z, relatively high Y). This avoids needing to modify the build functions.
 */
function _animateTailWag(entity, state) {
  if (!entity.mesh) return;

  // Cache the tail reference on first call
  if (entity._tailMesh === undefined) {
    entity._tailMesh = null;
    // Find the tail: it's a direct child of the group at negative Z
    for (const child of entity.mesh.children) {
      if (child.isMesh && child.position.z < -4) {
        entity._tailMesh = child;
        entity._tailBaseRotX = child.rotation.x;
        entity._tailBaseRotZ = child.rotation.z;
        break;
      }
    }
  }

  if (entity._tailMesh) {
    const t = state.breathPhase; // reuse breath phase for smooth timing
    entity._tailMesh.rotation.z = (entity._tailBaseRotZ || 0) + Math.sin(t * IDLE_TAIL_SPEED) * IDLE_TAIL_AMP;
  }
}

/**
 * Smoothly reset body tilt when transitioning away from swimming.
 */
function _resetBodyTilt(entity, dt) {
  if (entity.mesh && entity.mesh.rotation.x !== 0) {
    entity.mesh.rotation.x = lerp(entity.mesh.rotation.x, 0, dt * 4);
    if (Math.abs(entity.mesh.rotation.x) < 0.001) {
      entity.mesh.rotation.x = 0;
    }
  }
  // Also reset arm Z rotation from swimming
  if (entity.armL && entity.armL.rotation.z !== 0) {
    entity.armL.rotation.z = lerp(entity.armL.rotation.z, 0, dt * 4);
  }
  if (entity.armR && entity.armR.rotation.z !== 0) {
    entity.armR.rotation.z = lerp(entity.armR.rotation.z, 0, dt * 4);
  }
}

/**
 * Find the original hex color for a given material from the cached list.
 */
function _findOriginalColor(originalColors, material) {
  if (!originalColors) return null;
  for (const entry of originalColors) {
    if (entry.material === material) {
      return entry.color;
    }
  }
  return null;
}

/**
 * Restore all material colors to their originals after a hurt flash.
 */
function restoreColors(entity, state) {
  if (!state.originalColors || !entity.mesh) return;

  for (const entry of state.originalColors) {
    if (entry.material && entry.material.color) {
      entry.material.color.setHex(entry.color);
    }
  }

  state.originalColors = null;
}

/**
 * Set transparent = true on all materials in a mesh hierarchy.
 */
function _setMeshTransparent(mesh, transparent) {
  mesh.traverse((child) => {
    if (child.isMesh && child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of materials) {
        mat.transparent = transparent;
      }
    }
    // Also handle sprites (name tags)
    if (child.isSprite && child.material) {
      child.material.transparent = transparent;
    }
  });
}

/**
 * Set opacity on all materials in a mesh hierarchy.
 */
function _setMeshOpacity(mesh, opacity) {
  mesh.traverse((child) => {
    if (child.isMesh && child.material) {
      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of materials) {
        mat.opacity = opacity;
      }
    }
    if (child.isSprite && child.material) {
      child.material.opacity = opacity;
    }
  });
}

/**
 * Check if the death animation has completed (fully faded out).
 * Useful for EntityManager to know when to remove the entity.
 *
 * @param {Object} entity
 * @returns {boolean} true if the death animation is complete
 */
export function isDeathComplete(entity) {
  const state = entity._animState;
  if (!state || state.action !== 'death') return false;
  return state.deathProgress >= (DEATH_DURATION + DEATH_FADE_DELAY + DEATH_FADE_DURATION);
}
